import { format, isValid, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { DEFAULT_DATE_FORMATE } from '../constents';

export const removeHtmlTags = (htmlString) => {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlString;
  // Get the text content without HTML tags
  return tempElement.textContent || tempElement.innerText || '';
};
/** Medgre HTML uses <br>; MOD notes use \n. Only apply pre-line when there is no <br>. */
export const preLineClass = (html) =>
  /<br\s*\/?>/i.test(String(html || '')) ? '' : 'content-line-break';

export const useDebounce = (value, delay) => {
  const [dbValue, setDbValue] = useState('');
  useEffect(() => {
    let timeOut = setTimeout(() => {
      setDbValue(value);
    }, delay);

    return () => {
      clearTimeout(timeOut);
    };
  }, [value, delay]);
  return dbValue;
};

export const convertNumberToWords = (number) => {
  if (!number && typeof +number !== Number) return false;
  if (number === 0) return 'zero';

  const belowTwenty = [
    '',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
  ];

  const tens = [
    '',
    '',
    'twenty',
    'thirty',
    'forty',
    'fifty',
    'sixty',
    'seventy',
    'eighty',
    'ninety',
  ];

  const aboveThousand = ['', 'thousand', 'million', 'billion'];

  function helper(num) {
    if (num < 20) return belowTwenty[num];
    else if (num < 100)
      return (
        tens[Math.floor(num / 10)] +
        (num % 10 !== 0 ? ' ' + belowTwenty[num % 10] : '')
      );
    else
      return (
        belowTwenty[Math.floor(num / 100)] +
        ' hundred' +
        (num % 100 !== 0 ? ' ' + helper(num % 100) : '')
      );
  }

  let word = '';
  let unitIndex = 0;

  while (number > 0) {
    const chunk = number % 1000;
    if (chunk !== 0) {
      const chunkWord = helper(chunk);
      word =
        chunkWord +
        (aboveThousand[unitIndex] !== ''
          ? ' ' + aboveThousand[unitIndex]
          : '') +
        (word ? ' ' + word : '');
    }
    number = Math.floor(number / 1000);
    unitIndex++;
  }

  return word.trim();
};

export const ucFirst = (s) => {
  if (!s) return '';
  let str = s.toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const ucWords = (s) => (s && s.replace(/\w\S*/g, ucFirst)) || '';

export function zodErrorResolver(errorsArray) {
  const result = {};

  errorsArray &&
    errorsArray.forEach((error) => {
      const key = error.path[0];
      if (!result[key]) {
        result[key] = { message: error.message };
      }
    });

  return result;
}

export function isEmptyObject(obj) {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export const formatDateSafely = (date) => {
  const parsedDate = date instanceof Date ? date : parseISO(date);
  return isValid(parsedDate)
    ? format(parsedDate, 'yyyy-MM-dd')
    : 'Invalid Date';
};

export const stripHtml = (html) => {
  if (!html) return '';

  // Convert <br> tags to newlines
  let text = html.replace(/<br\s*\/?>/gi, '\n');

  // Convert <p> tags to newlines (consider both opening and closing for robustness)
  text = text.replace(/<\s*p[^>]*>/gi, ''); // Opening p
  text = text.replace(/<\s*\/p\s*>/gi, '\n'); // Closing p

  // Convert <div> tags to newlines (consider both opening and closing for robustness)
  text = text.replace(/<\s*div[^>]*>/gi, ''); // Opening div
  text = text.replace(/<\s*\/div\s*>/gi, '\n'); // Closing div

  // Remove all other HTML tags
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || '';
};
//mask date
export const getMaskDate = (value) => {
  if (!value) return '';
  let v = value.replace(/\D/g, '').slice(0, 8);
  if (v.length > 4) {
    v = `${v.slice(0, 2)}-${v.slice(2, 4)}-${v.slice(4)}`;
  } else if (v.length > 2) {
    v = `${v.slice(0, 2)}-${v.slice(2)}`;
  }
  return v;
};

//phone mobile fax input mask
export const getMaskPhone = (value) => {
  if (!value) return '';
  let v = value.replace(/\D/g, '').slice(0, 14);
  if (v.length > 3) {
    v = `(${v.slice(0, 3)}) ${v.slice(3)}`;
  }
  if (v.length > 9) {
    v = `${v.slice(0, 9)}-${v.slice(9)}`;
  }
  return v;
};

//mask time — supports H:MM / HH:MM and optional AM/PM (e.g. 930AM → 9:30 AM)
export const getMaskTime = (value) => {
  if (!value) return '';
  const raw = value.replace(/[^0-9a-zA-Z]/g, '').slice(0, 6);
  const match = raw.match(/^(\d{0,4})([aApP]?[mM]?)?$/);
  if (!match) return raw;
  const digits = match[1] || '';
  const ampm = (match[2] || '').toUpperCase();
  let timePart = digits;
  if (digits.length === 3) {
    timePart = `${digits.slice(0, 1)}:${digits.slice(1)}`;
  } else if (digits.length > 2) {
    timePart = `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  }
  return ampm ? `${timePart} ${ampm}` : timePart;
};
//mask zip
export const getMaskZip = (value) => {
  if (!value) return '';
  let v = value.replace(/\D/g, '').slice(0, 9);
  if (v.length > 5) {
    v = `${v.slice(0, 5)}-${v.slice(5)}`;
  }
  return v;
};

// short text
export const shortText = (text, length = 15) => {
  if (!text) return '';
  if (text.length > length) {
    return `${text.slice(0, length)}...`;
  }
  return text;
};

export const formatDateTime = (dateTime, defaultTimeFormat) => {
  if (!dateTime) return '';
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
  if (!isValid(date)) return '';

  const dateFormat = DEFAULT_DATE_FORMATE || 'MM-dd-yyyy';
  const timeFormat = defaultTimeFormat?.withoutSecond || 'hh:mm a';

  // Basic mapping of common PHP date formats to date-fns formats if they differ
  const formatMap = {
    'm-d-Y': 'MM-dd-yyyy',
    'd-m-Y': 'dd-MM-yyyy',
    'Y-m-d': 'yyyy-MM-dd',
    'm/d/Y': 'MM/dd/yyyy',
    'd/m/Y': 'dd/MM/yyyy',
    'Y/m/d': 'yyyy/MM/dd',
  };

  const finalDateFormat = formatMap[dateFormat] || dateFormat;

  return format(date, `${finalDateFormat} ${timeFormat}`);
};

export const formatTime = (dateTime, defaultTimeFormat) => {
  if (!dateTime) return '';
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
  if (!isValid(date)) return '';
  const timeFormat = defaultTimeFormat?.withoutSecond || 'hh:mm a';
  return format(date, timeFormat);
};
